import { Topbar } from '@/components/organisms/Topbar';
import { Skeleton } from '@/components/ui/skeleton';

/** Widths for the tab labels, in tab order: All, Sending limits, then the four types. */
const TAB_WIDTHS = ['w-8', 'w-24', 'w-20', 'w-32', 'w-20', 'w-32'];

/**
 * `/action-items` pending state, shaped like the loaded page so nothing reflows
 * when the data lands: the 22px headline and its description line, the
 * underline tab row, then one issue-group card — header (title, hint, status
 * pill) over rows of 32px icon tile, name + reason, "How to fix" toggle and a
 * 32px action button. Same paddings as `IssueGroupCard` / `IssueRow`. Also the
 * page's own first-load branch — one skeleton for both.
 */
export function ActionItemsPendingState() {
  return (
    <>
      <Topbar crumbs={[{ label: 'Account' }, { label: 'Action Items' }]} title="Action Items" />
      <div className="flex min-h-0 flex-1 flex-col overflow-auto" style={{ background: 'var(--canvas)' }}>
        <div className="mx-auto flex w-full flex-col gap-6 px-4 py-6 md:px-8" aria-busy="true" aria-live="polite">
          {/* Summary */}
          <div className="flex flex-col gap-2.5">
            <Skeleton className="h-7 w-80 max-w-full" />
            <Skeleton className="h-4 w-[30rem] max-w-full" />
          </div>

          {/* Tabs */}
          <div className="flex gap-6 overflow-hidden border-b border-border">
            {TAB_WIDTHS.map((w, i) => (
              <div key={i} className="flex h-10 shrink-0 items-center gap-2">
                <Skeleton className={`h-4 ${w}`} />
                <Skeleton className="h-5 w-6 rounded-full" />
              </div>
            ))}
          </div>

          {/* One issue group */}
          <div className="overflow-hidden rounded-[var(--r-lg)] border border-[color:var(--ink-200)] bg-[color:var(--paper)]">
            <div className="flex items-center gap-3 border-b border-[color:var(--ink-100)] px-[18px] py-[14px]">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3.5 w-72 max-w-full" />
              </div>
              <Skeleton className="h-6 w-28 rounded-full" />
            </div>
            {Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 border-b border-[color:var(--ink-100)] px-[18px] py-[14px] last:border-b-0"
              >
                <Skeleton className="size-8 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-56 max-w-full" />
                  <Skeleton className="h-3 w-40 max-w-full" />
                </div>
                <Skeleton className="hidden h-8 w-24 sm:block" />
                <Skeleton className="h-8 w-[84px]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
